import { FC, useMemo, useCallback } from 'react'
import { PeergroupPopupKind, RelationId } from 'interfaces/peergroups'
import { registerPeergroupPopupTimeoutActionCreator } from 'actions/sidebarActions'
import { ViewportSegment } from 'interfaces/viewport'
import { SidebarGroupsItem } from 'components/features/pfm/SidebarGroups/SidebarGroupsItem'
import { SidebarGroupsItemChevron } from 'components/features/pfm/SidebarGroups/SidebarGroupsItemChevron'
import { useShouldBlockRelation } from 'hooks/subscriptionHooks'
import { PremiumOnly } from 'components/features/pfm/PremiumOnly/PremiumOnly'


export type PeergroupMenuItemOwnProps = {
  relationId: 'member'
} | {
  relationId: RelationId
}

export type PeergroupMenuItemStateProps = {
  isTabletOrBelow: boolean
  isSelected: boolean
  isPopupShown: boolean
  anyPopupShown: boolean

  timeoutId: number | null

  viewportSegment: ViewportSegment
  kind: PeergroupPopupKind
  name: string
}

export type PeergroupMenuItemDispatchProps = {
  selectRelation: () => void
  openSubscriptionPane: () => void

  showPopup: (boundingRect: ClientRect) => void
  hidePopup: () => void
  hideSidebar: () => void

  registerTimeout: typeof registerPeergroupPopupTimeoutActionCreator
}

export type PeergroupMenuItemProps =
  & PeergroupMenuItemStateProps
  & PeergroupMenuItemDispatchProps
  & PeergroupMenuItemOwnProps

export const PeergroupMenuItem: FC<PeergroupMenuItemProps> = (props) => {
  const {
    relationId,
    isSelected, name,
    viewportSegment,
    isPopupShown, hidePopup,
    anyPopupShown,
    showPopup,
    timeoutId, registerTimeout,
    kind,
    hideSidebar,
    selectRelation,
    openSubscriptionPane,
  } = props

  const chevron = viewportSegment !== 'desktop' && (
    <SidebarGroupsItemChevron color='satin' />
  )

  const color = useMemo(() => {
    if (kind === 'peers') {
      return 'coral'
    }

    if (kind === 'others') {
      return 'merlot'
    }

    return 'oxford'
  }, [kind])

  const shouldBlockRelation = useShouldBlockRelation(relationId)

  const handleSelect = useCallback(() => {
    // we should handle it here, not in reducer, because in some cases selection shouldn't hide popup
    if (anyPopupShown) {
      hidePopup()
    }

    // we should handle it here, not in reducer, because in some cases selection shouldn't hide sidebar
    if (viewportSegment !== 'desktop') {
      hideSidebar()
    }

    if (shouldBlockRelation) {
      openSubscriptionPane()
    } else {
      selectRelation()
    }
  }, [hidePopup, hideSidebar, selectRelation, anyPopupShown, viewportSegment, shouldBlockRelation, openSubscriptionPane])

  return (
    <SidebarGroupsItem
      selected={ kind !== 'others' || isSelected }
      selectable={ kind === 'others' }
      icon={ chevron }
      color={ color }
      onSelect={ kind === 'others' && !isSelected ? handleSelect : undefined }
      isPopupShown={ isPopupShown }
      isAnyPopupShown={ anyPopupShown }
      onShowPopup={ showPopup }
      onHidePopup={ hidePopup }
      blockPopup={ shouldBlockRelation }
      hideTimeoutId={ timeoutId }
      onRegisterHideTimeout={ registerTimeout }
    >
      <PremiumOnly isBlocked={ !!shouldBlockRelation }>
        { name }
      </PremiumOnly>
    </SidebarGroupsItem>
  )
}
